"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { ArrowRight, Menu, Phone, User, X } from "lucide-react";
import { site } from "@/content/site";
import { ROUTES } from "@/config/routes";
import { MobileMenu } from "@/components/layout/MobileMenu";
import { useCurrentUser } from "@/lib/auth/useCurrentUser";

export function Header() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const isHome = pathname === ROUTES.HOME;
  const { data: user } = useCurrentUser();

  return (
    <div className="fixed inset-x-0 top-0 z-50">
      <header className={`transition-all duration-300 ${isHome ? "border-b border-white/10 bg-black/20 backdrop-blur-xl" : "bg-[var(--surface-raised)]/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.16)] border-b border-[var(--border-subtle)]"} text-[var(--white)]`}>
        <div className="mx-auto flex min-h-16 w-[calc(100%_-_clamp(40px,6vw,96px))] max-w-[1840px] items-center justify-between gap-4 py-3 lg:min-h-[86px]">
          <Link href={ROUTES.HOME} className="min-w-0 shrink-0" onClick={() => setOpen(false)}>
            <Image
              src="/brand/swargaseema-logo-official.png"
              alt="Swargaseema"
              width={242}
              height={40}
              priority
              className="h-auto w-[180px] object-contain sm:w-[210px] lg:w-[242px]"
            />
          </Link>

          <nav className="hidden min-w-0 flex-1 items-center justify-center gap-6 xl:flex" aria-label="Primary navigation">
            {site.nav.map((item) => {
              const isActive = item.href === ROUTES.HOME ? pathname === item.href : pathname.startsWith(item.href);

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`relative py-2 text-sm font-semibold transition hover:text-[var(--accent)] ${isActive ? "text-[var(--accent)] after:absolute after:inset-x-0 after:-bottom-1 after:h-0.5 after:rounded-full after:bg-[var(--accent)]" : "text-white"
                    }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden items-center gap-5 xl:flex">
            {user ? (
              <Link
                href={ROUTES.DASHBOARD || "/dashboard"}
                className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold transition hover:text-[var(--accent)]"
                title="Go to Dashboard"
              >
                <span className="grid size-8 place-items-center rounded-full bg-[var(--accent)] text-white font-bold text-xs uppercase shadow-sm border border-[var(--accent-strong)] overflow-hidden">
                  {user.profile?.avatar || (user as any).avatar ? (
                    <img src={user.profile?.avatar || (user as any).avatar} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <>
                      {user.profile?.firstName?.[0] || ""}{user.profile?.lastName?.[0] || ""}
                      {!(user.profile?.firstName || user.profile?.lastName) && user.username?.substring(0, 2)}
                    </>
                  )}
                </span>
                <span className="hidden lg:inline-block">
                  {user.profile?.firstName || user.username}
                </span>
              </Link>
            ) : (
              <Link
                href={ROUTES.LOGIN}
                className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold transition hover:text-[var(--accent)]"
              >
                <span className="grid size-8 place-items-center rounded-full border border-white/15 text-[var(--accent)]">
                  <User size={14} />
                </span>
                Login
              </Link>
            )}
            <a className="inline-flex items-center gap-3 whitespace-nowrap text-sm font-semibold hover:text-[var(--accent)] transition" href={`tel:${site.phone.replaceAll(" ", "")}`}>
              <span className="grid size-8 place-items-center rounded-full border border-white/15 text-[var(--accent)]">
                <Phone size={14} />
              </span>
              {site.phone}
            </a>
            <Link
              href={ROUTES.CONTACT_US}
              className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-[var(--accent)] px-4 text-sm font-black text-white transition hover:bg-[var(--accent-hover)]"
            >
              Enquire Now <ArrowRight size={16} />
            </Link>
          </div>

          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className="grid size-11 place-items-center rounded-lg border border-white/20 text-[var(--accent)] xl:hidden"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && <MobileMenu items={site.nav} onNavigate={() => setOpen(false)} />}
      </header>
    </div>
  );
}
